import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';

import type { PitchAnalysisConfig, SmoothingMode } from '@muzly/shared';

import { InfoTip } from './InfoTip';
import { DEFAULT_ANALYSIS, useSessionControl } from './sessionControlContext';

/**
 * config 튜닝 패널 — 분석 파라미터를 굴려 `updateConfig`로 돌고 있는 세션에 적용한다(#32).
 *
 * **상태는 이 패널이 갖지 않는다.** `SessionControlProvider`가 갖는다. 예전엔 여기서
 * `useState`로 들고 자기만의 mock 모듈에 적용했는데, 그 두 가지가 각각 버그였다:
 * (1) 그 mock 인스턴스는 세션과 무관해 브라우저에서 슬라이더를 굴려도 화면이 그대로였고,
 * (2) 세션 start는 프로덕션 기본값을 다시 만들어 튜닝값이 재시작마다 소실됐다(실기기 포함).
 * provider가 상태를 가지면 start와 런타임 적용이 같은 값을 본다.
 *
 * 값 입력은 **슬라이더 + 숫자 입력 둘 다**다. 슬라이더는 훑기용, 숫자는 정확한 값
 * (`min_frequency_hz`를 딱 65로) 용이다 — 드래그로 정수를 맞추는 건 측정이 아니다.
 */

type RangeField =
  | 'silenceThresholdDb'
  | 'confidenceThreshold'
  | 'minFrequencyHz'
  | 'maxFrequencyHz';

interface RangeSpec {
  field: RangeField;
  label: string;
  min: number;
  max: number;
  step: number;
  unit: string;
  /** "?" 툴팁: 무엇 / 올리면·내리면 / 권장값(KO 평이체). */
  tip: string;
  hint: string;
}

const RANGES: RangeSpec[] = [
  {
    field: 'silenceThresholdDb',
    label: 'silence_threshold_db',
    min: -90,
    max: -20,
    step: 1,
    unit: 'dBFS',
    tip: '이 음량보다 조용하면 아예 분석을 건너뜁니다. 유성/무성을 가르는 값이 **아닙니다** — 그건 confidence_threshold가 합니다. 실측에서 발성(−42~−30)과 무발성(−47~−41.5)은 음량이 겹쳐서, 이 값을 올리면 잡음이 아니라 작게 낸 발성이 잘립니다. 잡음이 음으로 잡히면 이걸 올리지 말고 confidence_threshold를 올리세요.',
    hint: '기본 −60 · 발성은 −42~−30',
  },
  {
    field: 'confidenceThreshold',
    label: 'confidence_threshold',
    min: 0,
    max: 1,
    step: 0.01,
    unit: '',
    tip: '음 판정에 필요한 최소 신뢰도입니다. 올릴수록 불확실한 음을 더 걸러내 곡선이 안정되지만, 너무 높이면 약한 음이 끊깁니다. 그래프에 구멍이 늘면 내리세요.',
    hint: '기본 0.5',
  },
  {
    field: 'minFrequencyHz',
    label: 'min_frequency_hz',
    min: 40,
    max: 300,
    step: 1,
    unit: 'Hz',
    tip: '탐색할 가장 낮은 음역입니다. 하이패스 필터의 차단 주파수로도 쓰이므로, 올리면 저역 웅웅거림이 실제로 걸러집니다. 다만 올린 만큼 진짜 저음도 무음으로 떨어집니다 — 75로 올리면 C2~D2를 못 잡습니다.',
    hint: '기본 65(C2) · 남성 최저 ~82(E2)',
  },
  {
    field: 'maxFrequencyHz',
    label: 'max_frequency_hz',
    min: 400,
    max: 2000,
    step: 10,
    unit: 'Hz',
    tip: '탐색할 가장 높은 음역입니다. 내리면 고역 잡음을 배제하지만 소프라노 고음을 놓칩니다.',
    hint: '기본 1100(C#6)',
  },
];

interface ChoiceSpec<T> {
  label: string;
  options: { value: T; label: string }[];
  tip: string;
}

const WINDOW_SIZES = [512, 1024, 2048, 4096];
const HOP_SIZES = [256, 512, 1024, 2048];

const SMOOTHING: ChoiceSpec<SmoothingMode> = {
  label: 'smoothing',
  options: [
    { value: 'off', label: 'off' },
    { value: 'light', label: 'light' },
    { value: 'medium', label: 'medium' },
  ],
  tip: '표시용 스무딩입니다. 짧은 median으로 한 프레임짜리 옥타브 튐을 걷어냅니다. off로 두면 엔진 raw 출력이 그대로 보입니다 — 튐을 찾을 때는 off가 맞습니다. **채점은 언제나 raw를 쓰므로 이 값과 무관합니다.**',
};

/** 기본값 대비 얼마나 벗어났는지 한 줄로. 같으면 null(배지 안 그림). */
const driftLabel = (
  field: keyof PitchAnalysisConfig,
  value: unknown,
): string | null => {
  const base = DEFAULT_ANALYSIS[field];
  if (base === value) {
    return null;
  }
  return `기본 ${String(base)}`;
};

interface FieldShellProps {
  label: string;
  tip: string;
  drift: string | null;
  onRevert: () => void;
  children: ReactNode;
  hint?: string;
  /** 지금 소스가 이 값을 안 쓰면 그 이유. 있으면 흐리게 + 이유를 적는다. */
  unsupported?: string | null;
}

const FieldShell = ({
  label,
  tip,
  drift,
  onRevert,
  children,
  hint,
  unsupported,
}: FieldShellProps) => (
  <div className={`flex flex-col gap-1.5 ${unsupported ? 'opacity-45' : ''}`}>
    <div className="flex items-center gap-1.5">
      <span className="font-mono text-xs text-foreground">{label}</span>
      <InfoTip label={`${label} 설명`}>{tip}</InfoTip>
      {drift && (
        <button
          type="button"
          onClick={onRevert}
          title="기본값으로 되돌리기"
          className="rounded-full border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5 font-mono text-[10px] font-medium text-amber-600 transition hover:bg-amber-500/20"
        >
          ↺ {drift}
        </button>
      )}
    </div>
    {children}
    {unsupported ? (
      <span className="text-[10px] text-amber-600">⃠ {unsupported}</span>
    ) : (
      hint && <span className="text-[10px] text-muted-foreground">{hint}</span>
    )}
  </div>
);

interface RangeRowProps {
  spec: RangeSpec;
  value: number;
  onChange: (value: number) => void;
  unsupported: string | null;
}

/** 슬라이더(훑기) + 숫자 입력(정확한 값). 숫자는 blur/Enter 때만 범위로 clamp한다. */
const RangeRow = ({ spec, value, onChange, unsupported }: RangeRowProps) => {
  const [draft, setDraft] = useState(String(value));

  // 되돌리기·프리셋 등 외부에서 값이 바뀌면 입력창도 따라간다.
  useEffect(() => setDraft(String(value)), [value]);

  const commit = (): void => {
    const parsed = Number(draft);
    if (!Number.isFinite(parsed)) {
      setDraft(String(value));
      return;
    }
    const clamped = Math.min(spec.max, Math.max(spec.min, parsed));
    setDraft(String(clamped));
    onChange(clamped);
  };

  return (
    <FieldShell
      label={spec.label}
      tip={spec.tip}
      hint={spec.hint}
      unsupported={unsupported}
      drift={driftLabel(spec.field, value)}
      onRevert={() => onChange(DEFAULT_ANALYSIS[spec.field])}
    >
      <div className="flex items-center gap-2">
        <input
          type="range"
          min={spec.min}
          max={spec.max}
          step={spec.step}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="range-touch min-w-0 flex-1 cursor-pointer"
          aria-label={`${spec.label} 슬라이더`}
        />
        <span className="flex shrink-0 items-baseline gap-1">
          <input
            type="number"
            min={spec.min}
            max={spec.max}
            step={spec.step}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                commit();
              }
            }}
            className="w-16 rounded-md border border-border/60 bg-background px-1.5 py-1 text-right font-mono text-xs text-foreground focus:border-primary/60 focus:outline-none"
            aria-label={`${spec.label} 값 직접 입력`}
          />
          {spec.unit && (
            <span className="w-8 font-mono text-[10px] text-muted-foreground">
              {spec.unit}
            </span>
          )}
        </span>
      </div>
    </FieldShell>
  );
};

interface ChoiceRowProps<T extends string | number> {
  label: string;
  tip: string;
  field: keyof PitchAnalysisConfig;
  options: { value: T; label: string }[];
  value: T;
  onChange: (value: T) => void;
  hint?: string;
  unsupported: string | null;
}

const ChoiceRow = <T extends string | number>({
  label,
  tip,
  field,
  options,
  value,
  onChange,
  hint,
  unsupported,
}: ChoiceRowProps<T>) => (
  <FieldShell
    label={label}
    tip={tip}
    hint={hint}
    unsupported={unsupported}
    drift={driftLabel(field, value)}
    onRevert={() => onChange(DEFAULT_ANALYSIS[field] as T)}
  >
    <div className="flex flex-wrap gap-1">
      {options.map((option) => (
        <button
          key={String(option.value)}
          type="button"
          onClick={() => onChange(option.value)}
          aria-pressed={option.value === value}
          className={`min-h-[32px] rounded-md border px-2.5 font-mono text-xs transition motion-safe:active:scale-95 ${
            option.value === value
              ? 'border-primary/40 bg-primary text-primary-foreground'
              : 'border-border/60 text-muted-foreground hover:bg-muted'
          }`}
        >
          {option.label}
        </button>
      ))}
    </div>
  </FieldShell>
);

/**
 * 지금 소스가 이 필드를 **실제로** 지키는지. 지키지 않는 노브를 살아 있는 것처럼 두면
 * 굴려 보고 "아무 일도 안 일어난다"로 끝난다 — 그게 죽은 노브다.
 */
const unsupportedReason = (
  field: keyof PitchAnalysisConfig,
  mode: TuningMode,
): string | null => {
  if (mode === 'native') {
    return null;
  }
  if (mode === 'mock') {
    return '데모 소스는 고정 프레임을 내보내 이 값을 보지 않습니다';
  }
  // browser mic(ACF) — 유성 게이트 3축과 창/홉은 실제로 쓰고, 나머지는 대응물이 없다.
  if (field === 'smoothing') {
    return '스무딩은 네이티브 후처리(C++)에만 있습니다';
  }
  if (field === 'algorithm') {
    return '브라우저는 항상 ACF입니다';
  }
  return null;
};

type TuningMode = 'native' | 'mock' | 'mic';

/** 지금 이 소스에서 튜닝이 실제로 눈에 보이는지 — 안 보이면 그렇다고 말한다. */
const SourceNotice = ({
  mode,
  isStopped,
}: {
  mode: TuningMode;
  isStopped: boolean;
}) => {
  if (mode === 'mock') {
    return (
      <p className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs leading-relaxed text-amber-700">
        <strong>데모 소스에서는 이 값들이 결과를 바꾸지 않습니다.</strong> 데모
        프레임은 confidence 0.95 · rms −18dBFS · 고정 음높이를 그대로 내보내므로
        임계값에 걸릴 것이 없습니다. 실제로 튜닝해 보려면{' '}
        <strong>세션 상태</strong> 카드에서 소스를 “내 목소리”로 바꾸고
        시작하세요.
      </p>
    );
  }
  if (mode === 'mic') {
    return (
      <p className="rounded-lg border border-border/60 bg-muted/40 px-3 py-2 text-xs leading-relaxed text-foreground">
        브라우저 마이크 · <strong>추정기는 ACF(엔진 아님)</strong>입니다.
        무음·음역·신뢰도 임계값과 창/홉은 실제로 작동하므로 “아—” 하면서 굴려
        보면 곡선이 끊기고 이어지는 게 보입니다. 다만 confidence는 선택된 lag의
        정규화 상관값이라{' '}
        <strong>실기기 MPM의 값과 같은 척도가 아닙니다</strong> — 숫자를 서로
        비교하지 말고 반응을 보세요.
      </p>
    );
  }
  return (
    <p className="text-xs text-muted-foreground">
      native ·{' '}
      {isStopped
        ? '대기 중 — 지금 고른 값으로 다음 시작이 열립니다(네이티브 재빌드 불필요).'
        : '변경 시 updateConfig로 즉시 적용됩니다.'}
    </p>
  );
};

export const ConfigTuningPanel = () => {
  const { isNative, isStopped, analysis, setAnalysis, source } =
    useSessionControl();
  const mode: TuningMode = isNative
    ? 'native'
    : source === 'mic'
      ? 'mic'
      : 'mock';
  const blocked = (field: keyof PitchAnalysisConfig): string | null =>
    unsupportedReason(field, mode);

  const patch = <K extends keyof PitchAnalysisConfig>(
    field: K,
    value: PitchAnalysisConfig[K],
  ): void => setAnalysis({ ...analysis, [field]: value });

  const dirty = (
    Object.keys(DEFAULT_ANALYSIS) as (keyof PitchAnalysisConfig)[]
  ).filter((key) => analysis[key] !== DEFAULT_ANALYSIS[key]);

  return (
    <div className="flex flex-col gap-4">
      <SourceNotice mode={mode} isStopped={isStopped} />

      {dirty.length > 0 && (
        <div className="flex items-center justify-between gap-2 rounded-lg bg-muted/50 px-3 py-2">
          <span className="text-xs text-foreground">
            기본값과 다른 항목 {dirty.length}개
          </span>
          <button
            type="button"
            onClick={() => setAnalysis(DEFAULT_ANALYSIS)}
            className="rounded-full border border-border/60 px-2.5 py-1 text-[11px] font-medium text-muted-foreground transition hover:bg-muted motion-safe:active:scale-95"
          >
            전부 기본값으로
          </button>
        </div>
      )}

      {RANGES.map((spec) => (
        <RangeRow
          key={spec.field}
          spec={spec}
          value={analysis[spec.field]}
          onChange={(value) => patch(spec.field, value)}
          unsupported={blocked(spec.field)}
        />
      ))}

      <ChoiceRow
        label={SMOOTHING.label}
        tip={SMOOTHING.tip}
        field="smoothing"
        options={SMOOTHING.options}
        value={analysis.smoothing}
        onChange={(value) => patch('smoothing', value)}
        unsupported={blocked('smoothing')}
      />

      {/*
       * window/hop은 즉시 반영되지만 대가가 있다 — `muzly_pitch_update_config`가 분석기와
       * 누적 버퍼를 새로 만들므로 바꾸는 순간 프레임이 한 번 끊긴다. 그래서 그렇다고 적는다.
       */}
      <ChoiceRow
        label="window_size"
        tip="한 번에 분석하는 표본 개수입니다. 크면 낮은 음까지 정확해지지만 반응이 느려지고, 작으면 빠르지만 저음이 부정확해집니다. 바꾸는 순간 누적 버퍼가 비워져 프레임이 한 번 끊깁니다."
        hint="기본 2048 (48kHz에서 ≈42.7ms)"
        field="windowSize"
        options={WINDOW_SIZES.map((v) => ({ value: v, label: String(v) }))}
        value={analysis.windowSize}
        onChange={(value) => patch('windowSize', value)}
        unsupported={blocked('windowSize')}
      />
      <ChoiceRow
        label="hop_size"
        tip="분석 창을 얼마씩 밀지입니다. 작으면 프레임이 촘촘해져(그래프가 부드러워짐) CPU를 더 씁니다. 바꾸는 순간 프레임이 한 번 끊깁니다."
        hint="기본 1024 (프레임 간격 ≈21.3ms)"
        field="hopSize"
        options={HOP_SIZES.map((v) => ({ value: v, label: String(v) }))}
        value={analysis.hopSize}
        onChange={(value) => patch('hopSize', value)}
        unsupported={blocked('hopSize')}
      />
    </div>
  );
};
